import { INVITE_DAYS, INVITE_MAX_USES, TESTER_MIN_AGE } from '@mogzo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import {
  ApiError,
  apiSend,
  PLAYER_SITE,
  type Project,
  type Tester,
  type TesterInvite,
} from '../lib/api';
import { formatDate, initials } from '../lib/format';
import { useApi } from '../lib/use-api';
import { Alert, Avatar, Button, cardClass, ErrorNotice, Loading } from './ui';

const selectClass =
  'min-h-11 rounded-[6px] border border-line bg-card px-3 text-ink focus:border-ink focus:outline-none';

const ENDED: Record<Exclude<TesterInvite['status'], 'active'>, string> = {
  expired: 'Expired',
  used_up: 'Used up',
  turned_off: 'Turned off',
};

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the link is on screen to copy by hand.
    }
  }
  return (
    <Button variant="secondary" onClick={copy} className="min-h-10 px-3.5 text-[13px]">
      <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
    </Button>
  );
}

/** Invite links and the people who joined with them: who can see the private test. */
export function Testers({ project }: { project: Project }) {
  const [reload, setReload] = useState(0);
  const result = useApi<{ invites: TesterInvite[]; testers: Tester[] }>(
    `/v1/dev/projects/${project.id}/testers`,
    reload,
  );
  const [days, setDays] = useState<number>(7);
  const [maxUses, setMaxUses] = useState<number>(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState(false);
  const archived = Boolean(project.archivedAt);
  const ready = result.status === 'ready';

  // "Invite testers" on the community page links here; jump down once the list has loaded.
  useEffect(() => {
    if (ready && window.location.hash === '#testers') {
      document.getElementById('testers')?.scrollIntoView();
    }
  }, [ready]);

  async function act(run: () => Promise<unknown>) {
    setError(null);
    try {
      await run();
      setReload((n) => n + 1);
    } catch (failed) {
      setError(failure(failed));
    }
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMade(false);
    await act(() =>
      apiSend('POST', `/v1/dev/projects/${project.id}/invites`, { days, maxUses }).then(() =>
        setMade(true),
      ),
    );
    setBusy(false);
  }

  function removeTester(tester: Tester) {
    if (!window.confirm(`Remove ${tester.name}? They lose access to the test; their posts stay.`))
      return;
    void act(() =>
      apiSend('POST', `/v1/dev/projects/${project.id}/testers/${tester.userId}/remove`),
    );
  }

  return (
    <>
      <section
        id="testers"
        aria-labelledby="invites-heading"
        className={`${cardClass} flex scroll-mt-6 flex-col gap-3.5 px-6 py-5`}
      >
        <div>
          <h2 id="invites-heading" className="text-[15px] font-semibold">
            Invite testers
          </h2>
          <p className="mt-1.5 text-sm leading-relaxed text-muted">
            Share a link. Whoever opens it and signs in asks to join, and you accept them on the
            Members tab of{' '}
            <a
              href={`${PLAYER_SITE}/test/${project.slug}?tab=members`}
              className="font-semibold text-ink underline"
            >
              your test community
            </a>
            . Testers need to be {TESTER_MIN_AGE} or older.
          </p>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
        {made && (
          <Alert tone="success">Link made. Copy it and send it to the people you want.</Alert>
        )}
        {!archived && (
          <form
            onSubmit={create}
            className="flex flex-wrap items-end gap-3 rounded-[6px] bg-field px-4 py-3.5"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="invite-days" className="text-[13px] font-semibold">
                Works for
              </label>
              <select
                id="invite-days"
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
                className={selectClass}
              >
                {INVITE_DAYS.map((d) => (
                  <option key={d} value={d}>
                    {d} day{d === 1 ? '' : 's'}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="invite-uses" className="text-[13px] font-semibold">
                Up to
              </label>
              <select
                id="invite-uses"
                value={maxUses}
                onChange={(event) => setMaxUses(Number(event.target.value))}
                className={selectClass}
              >
                {INVITE_MAX_USES.map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? 'person' : 'people'}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" disabled={busy}>
              {busy ? 'Making…' : 'Create invite link'}
            </Button>
          </form>
        )}

        {result.status === 'loading' && <Loading />}
        {result.status === 'error' && <ErrorNotice error={result.error} />}
        {result.status === 'ready' && result.data.invites.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-[13px] text-muted">
                  <th scope="col" className="py-2.5 pr-2 font-semibold">
                    Link
                  </th>
                  <th scope="col" className="px-2 py-2.5 font-semibold">
                    Used
                  </th>
                  <th scope="col" className="px-2 py-2.5 font-semibold">
                    Works until
                  </th>
                  <th scope="col" className="py-2.5 pl-2">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.data.invites.map((invite) => {
                  const active = invite.status === 'active';
                  return (
                    <tr
                      key={invite.id}
                      className={`border-t border-divider ${active ? '' : 'text-muted'}`}
                    >
                      <td className="py-3 pr-2 font-mono text-[13px]">
                        {invite.url.replace(/^https?:\/\//, '')}
                      </td>
                      <td className="px-2 py-3">
                        {invite.uses} of {invite.maxUses}
                      </td>
                      <td className="px-2 py-3">
                        {active
                          ? formatDate(invite.expiresAt)
                          : ENDED[invite.status as keyof typeof ENDED]}
                      </td>
                      <td className="py-3 pl-2 text-right whitespace-nowrap">
                        {active && (
                          <span className="inline-flex gap-1.5">
                            <CopyButton text={invite.url} />
                            <Button
                              variant="danger"
                              className="min-h-10 px-3.5 text-[13px]"
                              onClick={() =>
                                act(() =>
                                  apiSend(
                                    'POST',
                                    `/v1/dev/projects/${project.id}/invites/${invite.id}/turn-off`,
                                  ),
                                )
                              }
                            >
                              Turn off
                            </Button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {result.status === 'ready' && (
        <section aria-labelledby="testers-heading" className={`${cardClass} px-6 py-5`}>
          <h2 id="testers-heading" className="text-[15px] font-semibold">
            {result.data.testers.length} tester{result.data.testers.length === 1 ? '' : 's'}
          </h2>
          <p className="mt-1.5 mb-2.5 text-sm leading-relaxed text-muted">
            Testers see each other’s display names, never emails. Removing someone ends their
            access; their posts stay.
          </p>
          {result.data.testers.length === 0 ? (
            <p className="border-t border-divider pt-3 text-sm text-muted">
              Nobody yet. Testers show here once you accept them.
            </p>
          ) : (
            <ul>
              {result.data.testers.map((tester) => (
                <li
                  key={tester.userId}
                  className="flex flex-wrap items-center gap-3 border-t border-divider py-3"
                >
                  <Avatar text={initials(tester.name)} />
                  <span className="flex-[1_1_200px]">
                    <strong>{tester.name}</strong>{' '}
                    <span className="text-[13px] text-muted">
                      · joined {formatDate(tester.joinedAt)} ·{' '}
                      {tester.postCount
                        ? `${tester.postCount} post${tester.postCount === 1 ? '' : 's'}`
                        : 'no posts yet'}
                    </span>
                  </span>
                  <Button
                    variant="secondary"
                    className="min-h-10 px-3.5 text-[13px]"
                    onClick={() => removeTester(tester)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
